import { View, Text, Pressable, StyleSheet, Linking, Modal, ScrollView } from "react-native";
import { useCallback, useMemo, useState } from "react";
import { faChevronRight, faXmark } from "@fortawesome/free-solid-svg-icons";
import ContainsHTML from "../ContainsHTML";
import AppStore from "../../storage/appStore";
import { logError, FontAwesomeIcon, homeIconSizes, tabIconSize, os } from "../../platform";
import { STRINGS } from "../../constants/strings";
import { COLORS } from "../../theme/colors";
import Carousel from "../Carousel";

const ICON_STYLE = "width: 38px; height: 38px; filter: brightness(0) invert(1); display: block; margin: auto;";
const STRIP_HTML_REGEX = /<[^>]*>/g;

const stripHtml = (html) => html?.replace(STRIP_HTML_REGEX, "").trim() ?? "";

const Alerts = ({ alerts }) => {
  const setErrorMessage = AppStore((state) => state.setErrorMessage);
  const [modalAlert, setModalAlert] = useState(null);

  const handleLinkPress = useCallback(
    async (url) => {
      try {
        await Linking.openURL(url);
      } catch (error) {
        logError("alerts_link_failed", {
          message: error.message,
          stack: error.stack,
          url,
        });
        setErrorMessage(STRINGS.failedToOpenLink);
      }
    },
    [setErrorMessage]
  );

  const handleAlertPress = useCallback(
    (alert) => {
      const linkUrl = alert?.linkField?.[0]?.url;

      if (linkUrl) {
        // Has linkUrl - open external link
        handleLinkPress(linkUrl);
      } else {
        // No linkUrl - open modal to show full text with clickable links
        setModalAlert(alert);
      }
    },
    [handleLinkPress]
  );

  const handleCloseModal = useCallback(() => {
    setModalAlert(null);
  }, [setModalAlert]);

  const alertComponents = useMemo(() => {
    return (
      alerts
        ?.map((alert) => {
          if (!alert?.simpleRichText) return null;

          const linkUrl = alert?.linkField?.[0]?.url;
          const buttonLabel = alert?.linkField?.[0]?.text ?? STRINGS.learnMore;
          const bgStyle = alert.color === "Red" ? styles.containerRed : styles.containerGreen;

          return (
            <View key={alert?.id} style={styles.carouselSlide}>
              <View
                testID="alert-container"
                style={[styles.alertContainer, bgStyle]}
              >
                <View style={styles.alertContentRow}>
                  {alert?.icon?.[0]?.img && (
                    <View
                      style={styles.iconContainer}
                      accessible={true}
                      accessibilityRole="image"
                      accessibilityLabel="Alert icon"
                    >
                      <ContainsHTML
                        testID="contains-html"
                        text={alert?.icon?.[0]?.img?.replace(
                          /<img([^>]*?)>/,
                          `<img$1 alt="" role="presentation" style="${ICON_STYLE}">`
                        )}
                        style={bgStyle}
                      />
                    </View>
                  )}
                  <Pressable
                    style={[styles.textContainer, alert?.icon?.[0]?.img ? null : styles.textContainerNoIcon]}
                    onPress={() => handleAlertPress(alert)}
                    accessibilityRole="button"
                    accessibilityLabel={
                      linkUrl
                        ? `Open link for ${alert?.title}`
                        : `View full text for ${alert?.title}`
                    }
                  >
                    <View style={styles.richTextClip} pointerEvents="none">
                      <Text
                        testID="alert-rich-text"
                        style={styles.richTextStyle}
                        numberOfLines={2}
                        maxFontSizeMultiplier={1.2}
                      >
                        {stripHtml(alert?.simpleRichText)}
                      </Text>
                    </View>
                  </Pressable>
                </View>
                <Pressable
                  testID="alert-button"
                  style={styles.alertButton}
                  onPress={() => handleAlertPress(alert)}
                  accessibilityRole="button"
                  accessibilityLabel={
                    linkUrl
                      ? `Open link for ${alert?.title}`
                      : `View full text for ${alert?.title}`
                  }
                >
                  <Text style={styles.alertLink} maxFontSizeMultiplier={1.2}>
                    {buttonLabel}
                  </Text>
                  <FontAwesomeIcon icon={faChevronRight} size={homeIconSizes.button} color={COLORS.textLight} />
                </Pressable>
              </View>
            </View>
          );
        })
        .filter(Boolean) || []
    );
  }, [alerts, handleAlertPress]);

  if (!alerts || alerts.length === 0) {
    return null;
  }

  return (
    <View style={styles.carouselContainer}>
      {alerts.length === 1 ? (
        alertComponents[0]
      ) : (
        <Carousel
          testID="carousel-component"
          items={alertComponents}
          rightArrow={
            <View style={styles.carouselArrow}>
              <FontAwesomeIcon icon={faChevronRight} size={homeIconSizes.garbage} color={COLORS.textLight} />
            </View>
          }
        />
      )}

      <Modal
        animationType="fade"
        transparent={true}
        visible={!!modalAlert}
        onRequestClose={handleCloseModal}
        testID="alert-modal"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContentWrapper}>
            <View style={styles.modalContent}>
              <Pressable
                style={styles.modalCloseButton}
                onPress={handleCloseModal}
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel="Close alert"
              >
                <FontAwesomeIcon icon={faXmark} color={COLORS.tertiary} size={tabIconSize} />
              </Pressable>
              {modalAlert && (
                <ScrollView>
                  <ContainsHTML
                    testID="modal-rich-text"
                    text={modalAlert.simpleRichText}
                    style={styles.modalRichTextStyle}
                  />
                </ScrollView>
              )}
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  carouselContainer: {
    marginTop: -10,
    marginHorizontal: -10,
  },
  carouselSlide: {
    flex:1,
    width: "100%",
  },
  alertContainer: {
    flex: 1,
    marginBottom: 10,
  },
  alertContentRow: {
    flexDirection: "row",
    paddingTop: os === "web" ? 5 : 3,
  },
  iconContainer: {
    paddingTop: 5,
    paddingBottom:5,
    width: "17%",
  },
  textContainer: {
    width: "75%",
    justifyContent: "center",
  },
  textContainerNoIcon: {
    width: "90%",
    paddingLeft: 10,
  },
  richTextClip: {
    paddingBottom: os === "web" ? 5 : 3,
  },
  richTextStyle: {
    fontSize: 16,
    color: COLORS.textLight,
    fontWeight: "bold",
  },
  alertButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-end",
    width: 130,
    paddingBottom: os === "web" ? 3 : 12,
    paddingRight: 5,
  },
  alertLink: {
    fontSize: 16,
    color: COLORS.textLight,
  },
  containerRed: {
    fontSize: 16,
    color: COLORS.textLight,
    backgroundColor: COLORS.red,
  },
  containerGreen: {
    fontSize: 16,
    color: COLORS.textLight,
    backgroundColor: COLORS.vbGreen,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: COLORS.secondaryTranslucent,
  },
  modalContentWrapper: {
    width: "90%",
    ...(os === "web" ? { maxWidth: 750 } : {}),
    maxHeight: "80%",
    backgroundColor: COLORS.background || "#FFFFFF",
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  modalContent: {
    padding: 20,
    paddingTop: 40,
    width: "100%",
    ...(os === "web" ? { height: "100%" } : {}),
  },
  modalCloseButton: {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 10,
  },
  modalRichTextStyle: {
    fontSize: 16,
    color: COLORS.text,
  },
  carouselArrow: {
    paddingTop: os === "web" ? 5 : 10,
  },
});

export default Alerts;
